import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2, RefreshCw, Users, UserCheck, UserX, CreditCard, DollarSign,
  TrendingUp, FileText, Building2, Percent, ShieldAlert, AlertTriangle, BadgeCheck, Bell,
} from "lucide-react";
import { Link } from "react-router-dom";
import { AppShell } from "@/components/app/AppShell";
import { useAuth } from "@/context/AuthContext";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import api, { getErrorMessage } from "@/lib/api";
import { LiveVisitors } from "@/components/admin/LiveVisitors";

const REFRESH_MS = 60000;

const PLAN_BADGE = {
  free: "bg-muted text-muted-foreground",
  pro: "bg-[hsl(var(--risk-low-bg))] text-[hsl(var(--risk-low))]",
  pm: "bg-primary/10 text-primary",
};
const PLAN_LABEL = { free: "Free", pro: "Pro Host", pm: "Property Manager" };

const fmtMoney = (v, cur = "AUD") =>
  v === null || v === undefined ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ${cur}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—");

const StatCard = ({ icon: Icon, label, value, sub, accent = "text-primary", testid }) => (
  <div className="rounded-xl border bg-card p-5" data-testid={testid}>
    <div className="flex items-center justify-between">
      <p className="text-sm text-muted-foreground">{label}</p>
      <Icon className={`h-4 w-4 ${accent}`} />
    </div>
    <p className="mt-2 font-display text-2xl font-semibold" data-testid={`${testid}-value`}>{value}</p>
    {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
  </div>
);

const SectionTitle = ({ children }) => (
  <h2 className="mb-3 mt-8 font-display text-lg font-semibold first:mt-0">{children}</h2>
);

export default function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [unreadNotifs, setUnreadNotifs] = useState(0);

  // Guard: redirect non-admins
  useEffect(() => {
    if (!authLoading && user && !user.is_admin) {
      navigate("/app/dashboard", { replace: true });
    }
  }, [authLoading, user, navigate]);

  const load = useCallback(async (isManual = false) => {
    if (isManual) setRefreshing(true);
    try {
      const [s, u] = await Promise.all([api.get("/admin/stats"), api.get("/admin/users")]);
      setStats(s.data);
      setUsers(u.data.users || []);
      setError(null);
      setLastUpdated(new Date());
    } catch (e) {
      setError(getErrorMessage(e, "Failed to load admin data."));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Unread notification count for the dashboard badge.
  const loadUnread = useCallback(async () => {
    try {
      const r = await api.get("/admin/notifications/unread-count");
      setUnreadNotifs(r.data?.unread_count || 0);
    } catch (e) {
      /* non-critical */
    }
  }, []);

  useEffect(() => {
    if (user?.is_admin) {
      load();
      loadUnread();
      const id = setInterval(() => { load(); loadUnread(); }, REFRESH_MS);
      return () => clearInterval(id);
    }
  }, [user, load, loadUnread]);

  if (authLoading || (user && !user.is_admin)) {
    return (
      <AppShell title="Admin">
        <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      </AppShell>
    );
  }

  const rev = stats?.revenue;
  const usr = stats?.users;
  const ass = stats?.assessments;
  const prop = stats?.properties;
  const conv = stats?.conversion;

  return (
    <AppShell
      title="Admin Dashboard"
      action={
        <div className="flex items-center gap-2">
          <Link
            to="/app/admin/notifications"
            className="relative inline-flex items-center gap-1.5 rounded-lg border bg-card px-3 py-1.5 text-sm font-medium text-foreground hover:bg-accent"
            data-testid="admin-notifications-link"
            aria-label="Open notification centre"
          >
            <Bell className="h-4 w-4" /> Notifications
            {unreadNotifs > 0 && (
              <span
                className="ml-0.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-semibold text-primary-foreground"
                data-testid="admin-notifications-badge"
              >
                {unreadNotifs > 99 ? "99+" : unreadNotifs}
              </span>
            )}
          </Link>
          <button
            onClick={() => load(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border bg-card px-3 py-1.5 text-sm font-medium text-foreground hover:bg-accent"
            data-testid="admin-sync-stripe"
            aria-label="Sync Stripe now"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> Sync Stripe now
          </button>
        </div>
      }
    >
      <div className="space-y-2" data-testid="admin-dashboard">
        <div className="flex flex-wrap items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-primary" />
          {stats?.stripe_live ? (
            <Badge className="bg-[hsl(var(--risk-low-bg))] text-[hsl(var(--risk-low))]" data-testid="admin-source-badge">
              <BadgeCheck className="mr-1 h-3 w-3" /> Stripe live mode
            </Badge>
          ) : stats?.data_source === "stripe_test" ? (
            <Badge className="bg-[hsl(var(--risk-medium-bg))] text-[hsl(var(--risk-medium))]" data-testid="admin-source-badge">Stripe test mode</Badge>
          ) : (
            <Badge className="bg-[hsl(var(--risk-high-bg))] text-[hsl(var(--risk-high))]" data-testid="admin-source-badge">
              <AlertTriangle className="mr-1 h-3 w-3" /> Local database (Stripe unavailable)
            </Badge>
          )}
          <p className="text-sm text-muted-foreground">
            Subscription &amp; revenue figures are the source of truth from Stripe.
            {stats?.stripe_synced_at && (
              <span data-testid="admin-last-synced"> Last synced from Stripe: {new Date(stats.stripe_synced_at).toLocaleString()}.</span>
            )}
          </p>
        </div>
        {(stats?.excluded_accounts > 0 || stats?.excluded_subscriptions > 0) && (
          <p className="text-xs text-muted-foreground" data-testid="admin-excluded-note">
            Excluded from analytics: {stats.excluded_accounts} demo/test account(s)
            {stats.excluded_subscriptions ? `, ${stats.excluded_subscriptions} test subscription(s)` : ""}.
          </p>
        )}

        {error && (
          <div className="flex items-center gap-2 rounded-lg border border-[hsl(var(--risk-high))]/25 bg-[hsl(var(--risk-high-bg))] p-3 text-sm text-[hsl(var(--risk-high))]" data-testid="admin-error">
            <AlertTriangle className="h-4 w-4" /> {error}
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-2 gap-4 pt-4 sm:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
          </div>
        ) : (
          <>
            {/* LIVE VISITORS (real-time) */}
            <SectionTitle>Live visitors</SectionTitle>
            <LiveVisitors />

            {/* USER METRICS */}
            <SectionTitle>User metrics</SectionTitle>
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <StatCard icon={Users} label="Total registered users" value={usr.total} testid="admin-total-users" />
              <StatCard icon={Users} label="Free users" value={usr.free} accent="text-muted-foreground" testid="admin-free-users" />
              <StatCard icon={BadgeCheck} label="Pro Host subscribers" value={usr.pro} accent="text-[hsl(var(--risk-low))]" testid="admin-pro-users" />
              <StatCard icon={BadgeCheck} label="Property Manager subscribers" value={usr.pm} testid="admin-pm-users" />
              <StatCard icon={UserCheck} label="Active subscriptions" value={usr.active_subscriptions} sub={usr.cancelling ? `incl. ${usr.cancelling} cancelling` : "live from Stripe"} accent="text-[hsl(var(--risk-low))]" testid="admin-active-subs" />
              <StatCard icon={AlertTriangle} label="Cancelling (at period end)" value={usr.cancelling} sub="still active until period end" accent="text-[hsl(var(--risk-medium))]" testid="admin-cancelling-subs" />
              <StatCard icon={UserX} label="Cancelled subscriptions" value={usr.cancelled_subscriptions} accent="text-muted-foreground" testid="admin-cancelled-subs" />
              <StatCard icon={CreditCard} label="Trial users" value={usr.trial_users} testid="admin-trial-users" />
            </div>

            {/* REVENUE */}
            <SectionTitle>Revenue</SectionTitle>
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <StatCard icon={DollarSign} label="Monthly recurring revenue (MRR)" value={fmtMoney(rev.mrr, rev.currency)} testid="admin-mrr" />
              <StatCard icon={TrendingUp} label="Annual recurring revenue (ARR)" value={fmtMoney(rev.arr, rev.currency)} testid="admin-arr" />
              <StatCard icon={DollarSign} label="Revenue this month" value={fmtMoney(rev.this_month, rev.currency)} accent="text-[hsl(var(--risk-low))]" testid="admin-rev-this-month" />
              <StatCard icon={DollarSign} label="Revenue last month" value={fmtMoney(rev.last_month, rev.currency)} accent="text-muted-foreground" testid="admin-rev-last-month" />
            </div>

            {/* ASSESSMENTS */}
            <SectionTitle>Assessments</SectionTitle>
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
              <StatCard icon={FileText} label="Total assessments" value={ass.total} testid="admin-ass-total" />
              <StatCard icon={FileText} label="Assessments this month" value={ass.this_month} testid="admin-ass-month" />
              <StatCard icon={ShieldAlert} label="High-risk results" value={ass.high} accent="text-[hsl(var(--risk-high))]" testid="admin-ass-high" />
              <StatCard icon={ShieldAlert} label="Medium-risk results" value={ass.medium} accent="text-[hsl(var(--risk-medium))]" testid="admin-ass-medium" />
              <StatCard icon={ShieldAlert} label="Low-risk results" value={ass.low} accent="text-[hsl(var(--risk-low))]" testid="admin-ass-low" />
            </div>

            {/* PROPERTIES + CONVERSION */}
            <div className="grid gap-4 lg:grid-cols-2">
              <div>
                <SectionTitle>Properties</SectionTitle>
                <div className="grid grid-cols-2 gap-4">
                  <StatCard icon={Building2} label="Total properties monitored" value={prop.total} testid="admin-prop-total" />
                  <StatCard icon={Building2} label="Properties added this month" value={prop.this_month} testid="admin-prop-month" />
                </div>
              </div>
              <div>
                <SectionTitle>Conversion</SectionTitle>
                <div className="grid grid-cols-3 gap-4">
                  <StatCard icon={Percent} label="Free → Pro" value={`${conv.free_to_pro}%`} testid="admin-conv-pro" />
                  <StatCard icon={Percent} label="Free → PM" value={`${conv.free_to_pm}%`} testid="admin-conv-pm" />
                  <StatCard icon={Percent} label="Overall" value={`${conv.overall}%`} accent="text-[hsl(var(--risk-low))]" testid="admin-conv-overall" />
                </div>
              </div>
            </div>

            {/* USER MANAGEMENT */}
            <SectionTitle>User management</SectionTitle>
            <div className="overflow-hidden rounded-xl border bg-card" data-testid="admin-users-table">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Business</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Subscription</TableHead>
                    <TableHead className="text-right">Assessments</TableHead>
                    <TableHead>Joined</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} data-testid={`admin-user-row-${u.id}`}>
                      <TableCell className="font-medium">
                        {u.email}
                        {u.is_admin && <Badge className="ml-2 bg-primary/10 text-primary text-[10px]">Admin</Badge>}
                        {u.excluded && <Badge variant="secondary" className="ml-2 text-[10px]" data-testid="admin-user-excluded-badge">Test/Demo</Badge>}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{u.business_name || "—"}</TableCell>
                      <TableCell><Badge className={PLAN_BADGE[u.plan] || PLAN_BADGE.free}>{PLAN_LABEL[u.plan] || u.plan}</Badge></TableCell>
                      <TableCell className="text-muted-foreground">
                        {u.subscription_status
                          ? `${u.subscription_status}${u.cancel_at_period_end ? " (cancels)" : ""}`
                          : "—"}
                      </TableCell>
                      <TableCell className="text-right">{u.assessment_count}</TableCell>
                      <TableCell className="text-muted-foreground">{fmtDate(u.created_at)}</TableCell>
                    </TableRow>
                  ))}
                  {users.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">No users found.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
